import { tw } from "@careinfo/tailwind";
import { useSessionStorage } from "@uidotdev/usehooks";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import APIS from "../../constant/Endpoint";
import useGetHook from "../../customHooks/useGetHook";
import Card from "../Reusable/Card";
import Filter from "./Filter";
import StatusList from "./StatusList";

interface IFilterCombobox {
  id: string;
  name: string;
  [key: string]: any;
}

export interface IFilter {
  pickup_branch: IFilterCombobox | null;
  staff: IFilterCombobox | null;
  to_branch: IFilterCombobox | null;
  sender: IFilterCombobox | null;
  vendor: IFilterCombobox | null;
  date_range: string | null;
}

const Dashboard = (props: any) => {
  const { currentUserProfile, isLoading, handleClearCache } = props;
  const [localState, handleSetState] = useSessionStorage<IFilter>(
    "kontract-dashboard-overview",
    {
      pickup_branch: null,
      to_branch: null,
      vendor: null,
      date_range: null,
      staff: null,
      sender: null,
    }
  );

  const currentDateTime = new Date();
  const year = currentDateTime.getFullYear();
  const month = String(currentDateTime.getMonth() + 1).padStart(2, "0"); // Adding 1 because months are zero-based
  const startDay = String(currentDateTime.getDate()).padStart(2, "0");
  const endDay = String(currentDateTime.getDate()).padStart(2, "0");
  const startDate = `${year}-${month}-${startDay}`;
  const endDate = `${year}-${month}-${endDay}`;
  const [date, setDate] = useState(
    `${startDate}T00:00:00,${endDate}T23:59:59.999`
  );

  const { register, reset, setValue, getValues, watch, control } =
    useForm<IFilter>({
      defaultValues: {
        pickup_branch: currentUserProfile?.is_superuser
          ? localState?.pickup_branch
          : currentUserProfile?.default_branch,
        to_branch: localState?.to_branch ?? null,
        vendor: localState?.vendor ?? null,
        date_range: localState?.date_range ?? `${startDate},${endDate}`,
        staff: currentUserProfile?.is_superuser
          ? localState?.staff
          : currentUserProfile,
        sender: localState?.sender ?? null,
      },
    });

  const handleReset = () => {
    reset({
      pickup_branch: null,
      to_branch: null,
      vendor: null,
      date_range: null,
      staff: null,
      sender: null,
    });
  };

  const filterParams = watch();

  useEffect(() => {
    handleSetState({
      ...localState,
      ...filterParams,
      vendor: filterParams?.vendor && {
        id: filterParams.vendor?.id,
        name: filterParams.vendor?.name,
      },
    });
  }, [filterParams]); //eslint-disable-line

  const params = useCallback(() => {
    const newValue = {};
    for (const [key, value] of Object.entries(filterParams)) {
      if (value) {
        if (Array.isArray(value)) {
          newValue[key] = value.map((v) => v.value).join(",");
        } else if (value && typeof value === "object" && value.id) {
          newValue[key] = value.id;
        } else if (!value) {
          newValue[key] = null;
        } else {
          newValue[key] = value;
        }
      }
    }
    return newValue;
  }, [filterParams]);

  const {
    data: paynentMethodData,
    isLoading: isPaymentMethodLoading,
    isRefetching,
  } = useGetHook({
    queryKey: ["payment-method", JSON.stringify(filterParams)],
    url: APIS.PAYMENT_METHOD,
    params: { ...params(), is_active: true },
  });

  return (
    <div className={tw`mb-[50px]`}>
      <Card>
        <>
          <div
            className={tw`uppercase text-primary  font-semibold cursor-pointer mx-2 pt-2`}
          >
            Filter
          </div>
          <div className={tw`grid grid-cols-5 gap-x-8 px-4 pb-2 relative`}>
            <Filter
              setValue={setValue}
              getValues={getValues}
              setDate={setDate}
              control={control}
              watch={watch}
              handleReset={handleReset}
              adminOnly={!currentUserProfile?.is_superuser}
            />
          </div>
        </>
      </Card>
      <div className={tw`flex justify-end`}>
        <button
          type="button"
          disabled={isLoading || false}
          onClick={() => handleClearCache()}
          className={tw`text-primary font-semibold mb-2 mr-2 focus:outline-none`}
        >
          Refresh
        </button>
      </div>
      <StatusList
        paynentMethodData={paynentMethodData}
        filterParams={params()}
        register={register}
        setValue={setValue}
        getValue={getValues}
        isPaymentMethodLoading={isPaymentMethodLoading || isRefetching}
        date={date}
      />
    </div>
  );
};

export default Dashboard;
